import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

/**
 * Catch-all OAuth callback handler.
 *
 * Several broker OAuth flows redirect back to relative paths that may or may
 * not be prefixed with `/api/` depending on how the user configured the broker
 * developer dashboard. The Kubernetes ingress only routes `/api/*` to the
 * backend, so paths like `/zerodha/callback?request_token=...` would hit the
 * React app and 404.
 *
 * This component runs when the URL matches one of those patterns, captures the
 * query params, forwards to the proper `/api/broker/<broker>/callback`
 * endpoint, then redirects to the home page with a success/error flag.
 */
export default function OAuthCallbackHandler() {
  const [status, setStatus] = useState("processing"); // processing | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const url = new URL(window.location.href);
    const path = url.pathname.toLowerCase();
    const params = url.searchParams;

    let backendPath;
    if (path.includes("zerodha")) {
      backendPath = "/api/broker/zerodha/callback";
    } else if (path.includes("breeze") || path.includes("icici")) {
      backendPath = "/api/broker/breeze/callback";
    } else {
      setStatus("error");
      setMessage("Unknown OAuth callback path");
      return;
    }

    const qs = params.toString();
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    if (!params.get("request_token") && path.includes("zerodha")) {
      setStatus("error");
      setMessage(params.get("status") === "cancelled" ? "Zerodha login was cancelled" : "Zerodha did not return a request token");
      return;
    }

    const target = `${apiBase}${backendPath}?${qs}`;
    // Navigate to the backend instead of making an AJAX request. The browser
    // follows the backend's final success/error redirect, so a failed token
    // exchange can no longer be mistaken for a successful connection.
    window.location.replace(target);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] p-6" data-testid="oauth-callback-page">
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-8 max-w-sm w-full text-center">
        {status === "processing" && (
          <>
            <Loader2 className="animate-spin text-[var(--brand)] mx-auto mb-3" size={28} />
            <p className="text-sm text-white font-medium">Finalising broker connection…</p>
            <p className="text-[11px] text-[var(--text-muted)] mt-1">Exchanging request token</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="text-emerald-400 mx-auto mb-3" size={28} />
            <p className="text-sm text-white font-medium">{message || "Connected"}</p>
            <p className="text-[11px] text-[var(--text-muted)] mt-1">Redirecting…</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="text-red-400 mx-auto mb-3" size={28} />
            <p className="text-sm text-white font-medium">Connection failed</p>
            <p className="text-[11px] text-[var(--text-muted)] mt-2 break-words">{message}</p>
            <a href="/" className="text-[11px] text-[var(--brand)] hover:underline mt-3 inline-block">Back to dashboard</a>
          </>
        )}
      </div>
    </div>
  );
}
